// NOTE: adapted from us-frontend

import * as React from 'react';
import { PortalWithState } from 'react-portal';

// styles
import styles from './style/modal-styles.module.css';

// interfaces
interface IProps {
  afterClose?: () => void;
  buttonCTA?: string;
  buttonComponent?: React.ReactElement;
  buttonStyles?: string;
  children: React.ReactElement;
  closeAble?: boolean;
  onClickEvent?: () => void;
  open?: boolean;
  openCallback?: () => void;
  openOnMount?: boolean;
  returnButton: boolean;
  styles?: Record<string, unknown>;
  transparent?: boolean;
  updateModal?: boolean;
}

interface IState {
  data: unknown;
  opened: boolean;
}

interface IPortalProps {
  closePortal: () => void;
  openPortal: (evt: any) => void;
  portal: (jsx: JSX.Element) => JSX.Element;
}

class Modal extends React.Component<IProps, IState> {
  private modalRef = React.createRef();

  constructor(public props: IProps) {
    super(props);

    this.state = {
      opened: !!props.open,
      data: undefined
    };
  }

  public shouldComponentUpdate(nextProps: IProps, nextState: IState) {
    return (
      nextState.opened !== this.state.opened ||
      nextProps.updateModal !== this.props.updateModal ||
      nextProps.open != this.props.open);

  }

  UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    if (typeof nextProps.open !== 'undefined') {
      this.setState({ opened: nextProps.open });
    }
  }

  private keepItOpen = (open: boolean): void => {
    this.setState({ opened: open });
  };

  // Typescript doesn't play well with refs so we trick it into thinking that we dont
  // know what type the variable will be by giving it type any...after that its happy
  // that the closePortal method is alright to use.
  public closeModalExternally = (): void => {
    const modal = this.modalRef.current as any;
    modal.closePortal();
  };

  public openModalExternally = (): void => {
    const modal = this.modalRef.current as any;
    modal.openPortal();
  };

  public handleOverlayClick = (): void => {
    if (this.props.closeAble !== false) {
      this.closeModalExternally();
    }
  };

  public onOpenCallback = (): void => {
    if (this.props.openCallback) {
      this.props.openCallback();
    }
  };

  private createTriggerButton(): JSX.Element | null {
    const { buttonCTA, buttonComponent, buttonStyles, returnButton } =
    this.props;

    // Sometimes we want to return a button that triggers the modal other times
    // we don't as it will be done programatically.
    if (!returnButton) return null;

    // If we supply an actual button element...use it.
    if (buttonComponent) {
      return React.cloneElement(buttonComponent);
    }

    // If we don't supply an element we return a fairly generic one.
    return <button className={buttonStyles}>{buttonCTA}</button>;
  }

  private handleOpen = (): void => {
    this.onOpenCallback();

    this.keepItOpen(true);
  };

  private handleClose = (): void => {
    this.keepItOpen(false);

    if (this.props.afterClose) {
      this.props.afterClose();
    }
  };

  public render(): JSX.Element {
    const {
      children,
      closeAble,
      onClickEvent,
      styles: propStyles,
      transparent
    } = this.props;

    const triggerButton = this.createTriggerButton();

    const wrapperStyles = transparent ? '' : styles.wrapper;
    const overlayStyles = transparent ? '' : styles.overlay;
    const contentStyles = transparent ? '' : styles.content;

    return (
      <PortalWithState
        closeOnEsc={!!closeAble}
        closeOnOutsideClick
        onClose={this.handleClose}
        onOpen={this.handleOpen}
        ref={this.modalRef}>

				{({ closePortal, openPortal, portal }: IPortalProps) => {
          const triggerButtonAsCompoundComponent = (() => {
            if (!triggerButton) return null;

            return React.cloneElement(triggerButton, {
              onClick: (evt: any) => {
                openPortal(evt);

                if (onClickEvent) {
                  onClickEvent();
                }
              }
            });
          })();

          const childrenAsCompoundComponent = React.cloneElement(children, {
            closeModal: closePortal
          });

          return (
            <>
							{triggerButtonAsCompoundComponent}
							{portal(
                <div className={wrapperStyles}>
									<div
                    className={overlayStyles}
                    onClick={this.handleOverlayClick} />

									<div
                    className={contentStyles}
                    data-testid="modal"
                    style={{ ...propStyles }}>

										{childrenAsCompoundComponent}
									</div>
								</div>
              )}
						</>);

        }}
			</PortalWithState>);

  }
}

export default Modal;